import { useCallback } from "react";
import { Box, Text, Stack, Group, Divider, Slider, Checkbox, Switch, NumberInput, Tooltip } from "@mantine/core";
import {
  useViewerStore,
  useInstances,
  useIKEnabled,
  useIKGizmos,
  useIKPositionMask,
  useIKRotationMask,
  useHiddenGizmoIds,
  useJoints,
  useJointGroups,
  useSelectedGroupFilter,
  useSelfCollisionAvailable,
  useSelfCollisionEnabled,
  useCollidingLinkPairs,
  useJointSettingsExpanded,
  useMimicSettings,
} from "../../store";
import { useWebSocket } from "../../WebSocket";
import { JointNameEditor } from "./JointNameEditor";
import { EndCoordsSection } from "./EndCoordsSection";
import { JointSettingsPanel } from "./JointSettingsPanel";
import { JointGroupsSection } from "./JointGroupsSection";
import { buildSliderMarks, formatDisplayValue, getJointUnit, snapDisplayValue } from "./jointUnits";
import { useJointSliderControl } from "./useJointSliderControl";

export function IKControls() {
  const ikEnabled = useIKEnabled();
  const ikGizmos = useIKGizmos();
  const positionMask = useIKPositionMask();
  const rotationMask = useIKRotationMask();
  const hiddenGizmoIds = useHiddenGizmoIds();
  const selfCollisionEnabled = useSelfCollisionEnabled();
  const selfCollisionAvailable = useSelfCollisionAvailable();
  const collidingLinkPairs = useCollidingLinkPairs();
  const joints = useJoints();
  const jointGroups = useJointGroups();
  const selectedGroupFilter = useSelectedGroupFilter();
  const jointSettingsExpanded = useJointSettingsExpanded();
  const mimicSettings = useMimicSettings();
  const instances = useInstances();
  const setIKEnabled = useViewerStore((state) => state.setIKEnabled);
  const setSelfCollisionEnabled = useViewerStore((state) => state.setSelfCollisionEnabled);
  const setIKPositionMask = useViewerStore((state) => state.setIKPositionMask);
  const setIKRotationMask = useViewerStore((state) => state.setIKRotationMask);
  const toggleGizmoVisibility = useViewerStore((state) => state.toggleGizmoVisibility);
  const toggleJointSettingsExpanded = useViewerStore((state) => state.toggleJointSettingsExpanded);
  const {
    sendEnableIKMode,
    sendEnableSelfCollision,
    sendSetIKConstraints,
    sendRenameJoint,
    sendHighlightJoint,
  } = useWebSocket();

  // Helper to get descendant instance IDs for a given instance
  const getDescendantInstanceIds = useCallback((instanceId: string): Set<string> => {
    const descendants = new Set<string>();
    const findDescendants = (parentId: string) => {
      for (const inst of instances) {
        if (inst.parent_instance_id === parentId) {
          descendants.add(inst.instance_id);
          // Also add the prefix if it's different from instance_id
          if (inst.prefix && inst.prefix !== inst.instance_id) {
            descendants.add(inst.prefix);
          }
          findDescendants(inst.instance_id);
        }
      }
    };
    findDescendants(instanceId);
    return descendants;
  }, [instances]);

  // Helper to get instance ID/prefix from joint name (format: "prefix/joint_name")
  const getInstanceFromJointName = useCallback((jointName: string): string | null => {
    const parts = jointName.split("/");
    if (parts.length < 2) return null;
    return parts[0];
  }, []);

  const handleToggleIK = (enabled: boolean) => {
    setIKEnabled(enabled);
    sendEnableIKMode(enabled);
  };

  const handleToggleSelfCollision = (enabled: boolean) => {
    setSelfCollisionEnabled(enabled);
    sendEnableSelfCollision(enabled);
  };

  const handlePositionMaskChange = (index: number, value: boolean) => {
    const newMask: [boolean, boolean, boolean] = [...positionMask];
    newMask[index] = value;
    setIKPositionMask(newMask);
    sendSetIKConstraints(newMask, rotationMask);
  };

  const handleRotationMaskChange = (index: number, value: boolean) => {
    const newMask: [boolean, boolean, boolean] = [...rotationMask];
    newMask[index] = value;
    setIKRotationMask(newMask);
    sendSetIKConstraints(positionMask, newMask);
  };

  const {
    changeJointValue: handleJointValueInputChange,
    dragJointValue: handleJointSliderDragChange,
    commitJointValue: handleJointSliderChangeEnd,
    commitJointFromStore,
  } = useJointSliderControl();

  const getDescendantMimicOptions = useCallback((jointName: string) => {
    const jointInstanceId = getInstanceFromJointName(jointName);
    const jointInstance = instances.find(
      (inst) => inst.prefix === jointInstanceId || inst.instance_id === jointInstanceId
    );
    const descendantIds = jointInstance
      ? getDescendantInstanceIds(jointInstance.instance_id)
      : new Set<string>();

    const descendantJoints = joints.filter((jt) => {
      if (jt.name === jointName) {
        return false;
      }
      const jtInstanceId = getInstanceFromJointName(jt.name);
      return jtInstanceId !== null && descendantIds.has(jtInstanceId);
    });

    return descendantJoints.map((jt) => {
      const parts = jt.name.split("/");
      const prefix = parts.length > 1 ? parts[0] : "";
      const shortName = parts.length > 1 ? parts.slice(1).join("/") : jt.name;
      return {
        value: jt.name,
        label: jt.display_name ? `${prefix}: ${jt.display_name}` : `${prefix}: ${shortName}`,
      };
    });
  }, [getDescendantInstanceIds, getInstanceFromJointName, instances, joints]);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" fw={500}>Enable IK</Text>
        <Switch
          checked={ikEnabled}
          onChange={(e) => handleToggleIK(e.currentTarget.checked)}
        />
      </Group>

      <Group justify="space-between">
        <Text size="sm" fw={500}>Self-Collision</Text>
        <Switch
          checked={selfCollisionEnabled}
          disabled={!selfCollisionAvailable}
          onChange={(e) => handleToggleSelfCollision(e.currentTarget.checked)}
        />
      </Group>
      {!selfCollisionAvailable && (
        <Text size="xs" c="dimmed">
          python-fcl is unavailable; self-collision checking is disabled.
        </Text>
      )}
      {collidingLinkPairs.length > 0 && (
        <Box>
          <Text size="xs" c="dimmed" mb={4}>
            Colliding links
          </Text>
          {collidingLinkPairs.slice(0, 6).map((pair) => (
            <Text key={`${pair.link_a}|${pair.link_b}`} size="xs" style={{ fontFamily: "monospace" }}>
              {pair.link_a} × {pair.link_b}
            </Text>
          ))}
          {collidingLinkPairs.length > 6 && (
            <Text size="xs" c="dimmed">
              +{collidingLinkPairs.length - 6} more
            </Text>
          )}
        </Box>
      )}

      {ikEnabled && (
        <>
          <Text size="xs" c="dimmed">
            {ikGizmos.length} end-effector{ikGizmos.length !== 1 ? "s" : ""} detected
          </Text>

          {ikGizmos.length > 0 && (
            <>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Position Constraints</Text>
                <Group gap="xs">
                  <Checkbox
                    label="X"
                    size="xs"
                    checked={positionMask[0]}
                    onChange={(e) => handlePositionMaskChange(0, e.currentTarget.checked)}
                  />
                  <Checkbox
                    label="Y"
                    size="xs"
                    checked={positionMask[1]}
                    onChange={(e) => handlePositionMaskChange(1, e.currentTarget.checked)}
                  />
                  <Checkbox
                    label="Z"
                    size="xs"
                    checked={positionMask[2]}
                    onChange={(e) => handlePositionMaskChange(2, e.currentTarget.checked)}
                  />
                </Group>
              </Box>

              <Box>
                <Text size="xs" c="dimmed" mb={4}>Rotation Constraints</Text>
                <Group gap="xs">
                  <Checkbox
                    label="Roll"
                    size="xs"
                    checked={rotationMask[0]}
                    onChange={(e) => handleRotationMaskChange(0, e.currentTarget.checked)}
                  />
                  <Checkbox
                    label="Pitch"
                    size="xs"
                    checked={rotationMask[1]}
                    onChange={(e) => handleRotationMaskChange(1, e.currentTarget.checked)}
                  />
                  <Checkbox
                    label="Yaw"
                    size="xs"
                    checked={rotationMask[2]}
                    onChange={(e) => handleRotationMaskChange(2, e.currentTarget.checked)}
                  />
                </Group>
              </Box>

              <Divider my="xs" />

              <Text size="xs" c="dimmed">End-effectors:</Text>
              {ikGizmos.map((gizmo) => (
                <Group key={gizmo.gizmo_id} gap="xs" align="center">
                  <Checkbox
                    size="xs"
                    checked={!hiddenGizmoIds[gizmo.gizmo_id]}
                    onChange={() => toggleGizmoVisibility(gizmo.gizmo_id)}
                    label={
                      <Text size="xs" style={{ fontFamily: "monospace" }}>
                        {gizmo.link_name}
                      </Text>
                    }
                  />
                </Group>
              ))}
            </>
          )}
        </>
      )}

      {/* Joint Groups Section - always visible */}
      <Divider my="xs" label="Joint Groups" labelPosition="center" />
      <JointGroupsSection />

          {/* Joint Sliders */}
          {joints.length > 0 && (
            <>
              <Divider my="xs" label="Joint Angles" labelPosition="center" />
              <Text size="xs" c="dimmed" mb="xs">
                Click on a joint name to rename it
              </Text>
              {joints
                // Filter by selected group
                .filter((joint) => {
                  if (!selectedGroupFilter) return true;
                  return joint.groups.includes(selectedGroupFilter);
                })
                .map((joint) => {
                  const unit = getJointUnit(joint.joint_type);
                  const minDisplay = unit.toDisplay(joint.min_angle);
                  const maxDisplay = unit.toDisplay(joint.max_angle);
                  const sliderMin = Math.min(minDisplay, maxDisplay);
                  const sliderMax = Math.max(minDisplay, maxDisplay);
                  const displayValue = unit.toDisplay(joint.current_angle);
                  const marks = buildSliderMarks(unit, joint.min_angle, joint.max_angle);
                  const mimicTargetOptions = getDescendantMimicOptions(joint.name);
                  const jointGroupBadges = joint.groups
                    .map((groupName) => {
                      const group = jointGroups.find((g) => g.name === groupName);
                      if (!group?.color) {
                        return null;
                      }
                      return { name: groupName, color: group.color };
                    })
                    .filter((badge): badge is { name: string; color: string } => badge !== null);

                  return (
                    <Box key={joint.name} mb="sm">
                      <Group justify="space-between" align="center" mb={4} wrap="nowrap">
                        <Group gap={4} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                          <JointNameEditor
                            joint={joint}
                            onRename={(newName) => sendRenameJoint(joint.name, newName)}
                            onHighlight={() => sendHighlightJoint(joint.name)}
                            onClearHighlight={() => sendHighlightJoint("")}
                          />
                          {/* Group badges */}
                          {jointGroupBadges.map((badge) => (
                            <Tooltip key={`${joint.name}-${badge.name}`} label={badge.name}>
                              <Box
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: badge.color,
                                }}
                              />
                            </Tooltip>
                          ))}
                        </Group>
                        <Group gap={4} wrap="nowrap">
                          <NumberInput
                            size="xs"
                            value={displayValue}
                            min={sliderMin}
                            max={sliderMax}
                            step={unit.step}
                            decimalScale={unit.decimals}
                            onChange={(value) => {
                              if (typeof value !== "number" || Number.isNaN(value)) {
                                return;
                              }
                              const clamped = Math.max(sliderMin, Math.min(sliderMax, value));
                              handleJointValueInputChange(joint.name, unit.toNative(clamped));
                            }}
                            onBlur={() => commitJointFromStore(joint.name)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                commitJointFromStore(joint.name);
                              }
                            }}
                            w={68}
                            styles={{ input: { fontSize: 10, textAlign: "right" } }}
                            hideControls
                          />
                          <Text size="xs" c="dimmed" style={{ minWidth: 18 }}>
                            {unit.unit}
                          </Text>
                          <JointSettingsPanel
                            joint={joint}
                            expanded={jointSettingsExpanded[joint.name] || false}
                            onToggle={() => toggleJointSettingsExpanded(joint.name)}
                            mimicSetting={mimicSettings[joint.name]}
                            mimicTargetOptions={mimicTargetOptions}
                          />
                        </Group>
                      </Group>
                      <Slider
                        value={displayValue}
                        onChange={(value) => {
                          const snapped = snapDisplayValue(value, unit, joint.min_angle, joint.max_angle);
                          handleJointSliderDragChange(joint.name, unit.toNative(snapped));
                        }}
                        onChangeEnd={(value) => {
                          const snapped = snapDisplayValue(value, unit, joint.min_angle, joint.max_angle);
                          handleJointSliderChangeEnd(joint.name, unit.toNative(snapped));
                        }}
                        min={sliderMin}
                        max={sliderMax}
                        step={unit.step}
                        marks={marks}
                        label={(value) => formatDisplayValue(value, unit)}
                        size="sm"
                      />
                    </Box>
                  );
                })}
            </>
          )}

      {/* End Coords Section - always visible when modules exist */}
      <Divider my="xs" label="End Coords" labelPosition="center" />
      <EndCoordsSection />
    </Stack>
  );
}
